import React, { useState, useEffect } from 'react';
import { QuotaService } from '../services/quotaService';

interface QuotaDisplayProps {
  refreshTrigger?: number; // Optional trigger to refresh quota after segment generation
}

const QuotaDisplay: React.FC<QuotaDisplayProps> = ({ refreshTrigger }) => {
  const [remaining, setRemaining] = useState<number>(0);
  const [total, setTotal] = useState<number>(3);
  const [resetInfo, setResetInfo] = useState<{ countdown: string; exactTime: string }>({ 
    countdown: '', 
    exactTime: '' 
  });

  // Calculate time until midnight Pacific Time (quota reset)
  const calculateResetTime = () => {
    const now = new Date();
    
    // Get current time in Pacific Time
    const pacificTimeStr = now.toLocaleString("en-US", { timeZone: "America/Los_Angeles" });
    const pacificTime = new Date(pacificTimeStr);
    
    // Calculate next midnight Pacific Time
    const tomorrowPacific = new Date(pacificTime);
    tomorrowPacific.setDate(tomorrowPacific.getDate() + 1);
    tomorrowPacific.setHours(0, 0, 0, 0);
    
    // Convert back to local time for calculation
    const localTimeStr = now.toLocaleString("en-US");
    const localTime = new Date(localTimeStr);
    const pacificOffset = pacificTime.getTime() - localTime.getTime();
    
    const resetTimeLocal = new Date(tomorrowPacific.getTime() - pacificOffset);
    const timeDiff = resetTimeLocal.getTime() - now.getTime();
    
    const hours = Math.floor(timeDiff / (1000 * 60 * 60));
    const minutes = Math.floor((timeDiff % (1000 * 60 * 60)) / (1000 * 60));
    
    // Also return the exact reset time for display
    const resetTimeStr = resetTimeLocal.toLocaleTimeString('ko-KR', { 
      hour: '2-digit', 
      minute: '2-digit',
      hour12: true 
    });
    
    return {
      countdown: `${hours}시간 ${minutes}분 후`,
      exactTime: resetTimeStr
    };
  };

  const updateQuota = async () => {
    try {
      const quota = await QuotaService.getQuota();
      setRemaining(quota.remaining || (quota.total - quota.used));
      setTotal(quota.total);
      setResetInfo(calculateResetTime());
    } catch (error) {
      console.error('Failed to fetch quota:', error);
      // Fallback to default values
      setRemaining(0);
      setTotal(3);
      setResetInfo(calculateResetTime());
    }
  };

  useEffect(() => {
    // Check quota on component mount
    updateQuota();
    
    // Update reset time every minute
    const interval = setInterval(() => {
      setResetInfo(calculateResetTime());
    }, 60000); // Update every minute
    
    // Also update when localStorage changes (for cross-tab sync)
    const handleStorageChange = () => updateQuota();
    window.addEventListener('storage', handleStorageChange);
    
    return () => {
      clearInterval(interval);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  // Update quota when refreshTrigger changes (after segment generation)
  useEffect(() => {
    if (refreshTrigger && refreshTrigger > 0) {
      updateQuota();
    }
  }, [refreshTrigger]);

  const percentage = (remaining / total) * 100;
  const isLow = remaining <= 1;
  const isEmpty = remaining === 0;

  return (
    <div className="bg-slate-800/60 p-4 rounded-xl border border-slate-700">
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-sm font-semibold text-slate-300">
          Veo 3 일일 생성 한도
        </h4>
        <span className={`text-lg font-bold ${
          isEmpty ? 'text-red-400' : isLow ? 'text-yellow-400' : 'text-green-400'
        }`}>
          {remaining}/{total}
        </span>
      </div>
      
      <div className="w-full bg-slate-700 rounded-full h-2 overflow-hidden">
        <div 
          className={`h-full transition-all duration-300 ${
            isEmpty ? 'bg-red-500' : isLow ? 'bg-yellow-500' : 'bg-green-500'
          }`}
          style={{ width: `${percentage}%` }}
        />
      </div>
      
      <p className="text-xs text-slate-400 mt-2">
        {isEmpty 
          ? `오늘의 생성 한도를 모두 사용했습니다. ${resetInfo.countdown} 복원됩니다.` 
          : `남은 비디오 생성: ${remaining}개`
        }
      </p>
      
      {/* Reset time info */}
      {resetInfo.countdown && (
        <div className="text-xs text-slate-500 mt-1">
          <p>⏰ 다음 초기화: {resetInfo.countdown}</p>
          <p className="text-slate-600">한국 시간 {resetInfo.exactTime} (태평양 시간 자정)</p>
        </div>
      )}
      
      {remaining > 0 && remaining <= 3 && (
        <p className="text-xs text-indigo-400 mt-1">
          💡 {remaining}개 영상으로 {remaining === 3 ? '24초' : remaining === 2 ? '16초' : '8초'} 스토리 완성 가능
        </p>
      )}
    </div>
  );
};

export default QuotaDisplay;